'use client';
import { bindDocumentNavigationToActiveAccountWhenBackgrounded } from '#pplx/frontend/browser-storage/accountNavigation.js';
import { safeRandomUUID } from '#pplx/frontend/common/safeRandomUUID.js';
import { sanitizedOpenWindow, sanitizedRedirectWindow, } from '#pplx/frontend/common/sanitizedRedirectWindow.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useNavigationGuards } from '#pplx/frontend/router/navigationGuard.js';
import { RouterContext } from '#pplx/frontend/router/RouterContext.js';
import { absolutePath, matchRoute, relativePath, toURL } from '#pplx/frontend/router/UrlUtils.js';
import React, { useCallback, useContext, useMemo, useRef } from 'react';
import { useRouter as useWouterRouter } from 'wouter';
const EMPTY_ARRAY = [];
const ROUTER_HISTORY_STATE_KEY = '__pplxRouterSessionId';
const ROUTER_BACK_PATH_HISTORY_STATE_KEY = '__pplxRouterBackPath';
let routerHistorySessionId;
function isHistoryStateRecord(state) {
    return typeof state === 'object' && state !== null;
}
function withRouterHistoryState(state = {}, backPath) {
    routerHistorySessionId ??= safeRandomUUID();
    return {
        ...state,
        ...(backPath === undefined ? {} : { [ROUTER_BACK_PATH_HISTORY_STATE_KEY]: backPath }),
        [ROUTER_HISTORY_STATE_KEY]: routerHistorySessionId,
    };
}
function isCurrentRouterSessionHistoryState(state) {
    if (!isHistoryStateRecord(state) ||
        typeof state[ROUTER_HISTORY_STATE_KEY] !== 'string' ||
        routerHistorySessionId === undefined) {
        return false;
    }
    return state[ROUTER_HISTORY_STATE_KEY] === routerHistorySessionId;
}
function isCurrentRouterSessionHistoryEntry() {
    return isCurrentRouterSessionHistoryState(window.history.state);
}
function getCurrentRouterSessionBackPath() {
    if (typeof window === 'undefined')
        return undefined;
    const state = window.history.state;
    if (!isCurrentRouterSessionHistoryState(state))
        return undefined;
    const backPath = state[ROUTER_BACK_PATH_HISTORY_STATE_KEY];
    return typeof backPath === 'string' ? backPath : undefined;
}
/**
 * Checks if a path is a SPA route.
 * @param spaRoutes - The SPA routes.
 * @param parser - The parser.
 * @param pathname - The pathname to check.
 * @returns True if the path is a SPA route, false otherwise.
 */
function isSPARoute(spaRoutes, parser, pathname) {
    return !!matchRoute(spaRoutes, pathname, parser);
}
function resolveNavigationTarget(path, base, spaRoutes, parser) {
    const unresolvedTarget = {
        absolutePath: path,
        pathWithoutBase: path,
        isTargetSpaRoute: false,
    };
    if (typeof window === 'undefined') {
        return unresolvedTarget;
    }
    let pathUrl;
    let isAbsoluteUrl = true;
    try {
        pathUrl = new URL(path);
    }
    catch {
        isAbsoluteUrl = false;
        try {
            pathUrl = toURL(path);
        }
        catch {
            return unresolvedTarget;
        }
    }
    if (pathUrl.origin !== window.location.origin) {
        return {
            absolutePath: pathUrl.toString(),
            pathWithoutBase: pathUrl.toString(),
            isTargetSpaRoute: false,
        };
    }
    // A fully qualified URL outside this shell is already browser-absolute. Matching its
    // root path against app-relative routes can incorrectly prepend the shell base.
    if (isAbsoluteUrl &&
        base !== '' &&
        base !== '/' &&
        pathUrl.pathname !== base &&
        !pathUrl.pathname.startsWith(`${base}/`)) {
        return unresolvedTarget;
    }
    const targetRelativePath = relativePath(pathUrl.pathname, base);
    const isTargetSpaRoute = isSPARoute(spaRoutes, parser, targetRelativePath);
    return {
        absolutePath: isTargetSpaRoute
            ? absolutePath(pathUrl.pathname, base, pathUrl.search, pathUrl.hash)
            : `${targetRelativePath}${pathUrl.search}${pathUrl.hash}`,
        pathWithoutBase: `${targetRelativePath}${pathUrl.search}${pathUrl.hash}`,
        isTargetSpaRoute,
    };
}
/**
 * Checks if a path should be navigated to using CSR.
 * @param path - The path to check.
 * @param base - The base path.
 * @param spaRoutes - The SPA routes.
 * @param parser - The parser.
 * @returns True if the path should be navigated to using CSR, false otherwise.
 */
function shouldNavigateCSR(path, base, spaRoutes, parser) {
    const currentUrl = toURL(window.location.href);
    const resolvedTarget = resolveNavigationTarget(path, base, spaRoutes, parser);
    const currentRelativePath = relativePath(currentUrl.pathname, base);
    return {
        ...resolvedTarget,
        shouldNavigate: resolvedTarget.isTargetSpaRoute && isSPARoute(spaRoutes, parser, currentRelativePath),
    };
}
export function useRouter() {
    const { requestNavigation } = useNavigationGuards();
    const { parser, base } = useWouterRouter();
    // This is intentionally optional bc SPA routes are not required
    const context = useContext(RouterContext);
    const spaRoutes = context?.spaRoutes ?? EMPTY_ARRAY;
    const openNonSpaRouteInNewTab = context?.openNonSpaRouteInNewTab ?? false;
    const isSpaApp = !!context?.spaRoutes;
    const startTimeFromTimeOriginRef = context?.startTimeFromTimeOriginRef;
    const appRef = context?.appRef;
    const push = useCallback(
    /**
     * Pushes a new route to the router.
     * @param path - The path to push. NOTE: This has to be a relative path.
     * @param opts - The options to pass to the router.push method.
     * @param reason - The reason for the push.
     * @returns A promise that resolves when the push is complete.
     */
    (path, opts = {}, reason) => {
        startTimeFromTimeOriginRef.current = performance.now();
        const currentUrl = toURL(window.location.href);
        logger.debug('Pushing route', {
            reason,
            path,
            previousPath: currentUrl.pathname,
            referrer: document.referrer,
            previousSearchParams: currentUrl.searchParams.toString(),
            previousUrl: currentUrl.toString(),
        });
        const { shouldNavigate, absolutePath, pathWithoutBase, isTargetSpaRoute } = shouldNavigateCSR(path, base, spaRoutes, parser);
        if (isSpaApp && openNonSpaRouteInNewTab && !isTargetSpaRoute) {
            sanitizedOpenWindow(bindDocumentNavigationToActiveAccountWhenBackgrounded(pathWithoutBase));
        }
        else {
            const navigate = () => {
                if (appRef?.current?.isOutdated() && appRef.current?.canRefresh()) {
                    appRef.current.refresh(bindDocumentNavigationToActiveAccountWhenBackgrounded(absolutePath));
                }
                else if (shouldNavigate) {
                    const currentRelativePath = relativePath(currentUrl.pathname, base) || '/';
                    const backPath = `${currentRelativePath}${currentUrl.search}${currentUrl.hash}`;
                    window.history.pushState(withRouterHistoryState(opts.state, backPath), '', absolutePath);
                }
                else {
                    sanitizedRedirectWindow(bindDocumentNavigationToActiveAccountWhenBackgrounded(absolutePath));
                }
            };
            if (new URL(absolutePath, currentUrl).pathname === currentUrl.pathname)
                navigate();
            else
                requestNavigation(navigate);
        }
    }, [
        startTimeFromTimeOriginRef,
        base,
        spaRoutes,
        parser,
        isSpaApp,
        openNonSpaRouteInNewTab,
        appRef,
        requestNavigation,
    ]);
    const refresh = useCallback((reason) => {
        startTimeFromTimeOriginRef.current = performance.now();
        const currentUrl = toURL(window.location.href);
        logger.debug('Refreshing route', {
            reason,
            path: window.location.pathname,
            referrer: document.referrer,
            previousSearchParams: currentUrl.searchParams.toString(),
            previousUrl: currentUrl.toString(),
        });
        if (appRef?.current?.isOutdated() && appRef.current?.canRefresh()) {
            appRef.current.refresh();
        }
        else {
            window.location.reload();
        }
    }, [startTimeFromTimeOriginRef, appRef]);
    const replace = useCallback((path, reason) => {
        const currentUrl = toURL(window.location.href);
        logger.debug('Replacing route', {
            reason,
            path,
            previousPath: currentUrl.pathname,
            referrer: document.referrer,
            previousSearchParams: currentUrl.searchParams.toString(),
            previousUrl: currentUrl.toString(),
        });
        const { absolutePath, pathWithoutBase, isTargetSpaRoute } = shouldNavigateCSR(path, base, spaRoutes, parser);
        if (isSpaApp && openNonSpaRouteInNewTab && !isTargetSpaRoute) {
            sanitizedOpenWindow(bindDocumentNavigationToActiveAccountWhenBackgrounded(pathWithoutBase));
        }
        else {
            const currentHistoryState = window.history.state;
            const historyState = isCurrentRouterSessionHistoryState(currentHistoryState)
                ? withRouterHistoryState(currentHistoryState)
                : {};
            const navigate = () => window.history.replaceState(historyState, '', absolutePath);
            if (new URL(absolutePath, currentUrl).pathname === currentUrl.pathname)
                navigate();
            else
                requestNavigation(navigate);
        }
    }, [base, parser, spaRoutes, isSpaApp, openNonSpaRouteInNewTab, requestNavigation]);
    const back = useCallback((reason, opts) => {
        startTimeFromTimeOriginRef.current = performance.now();
        const currentUrl = toURL(window.location.href);
        const canUseBrowserBack = isCurrentRouterSessionHistoryEntry();
        logger.debug('Navigating back', {
            reason,
            fallbackPath: opts?.fallbackPath,
            canUseBrowserBack,
            path: currentUrl.pathname,
            referrer: document.referrer,
            previousSearchParams: currentUrl.searchParams.toString(),
            previousUrl: currentUrl.toString(),
        });
        if (!canUseBrowserBack && opts?.fallbackPath) {
            opts.onNavigate?.('fallback');
            const fallbackReason = opts.fallbackReason ?? reason;
            if (opts.fallbackMode === 'replace') {
                replace(opts.fallbackPath, fallbackReason);
                return;
            }
            push(opts.fallbackPath, undefined, fallbackReason);
            return;
        }
        requestNavigation(() => {
            opts?.onNavigate?.('browserHistory');
            window.history.back();
        });
    }, [push, replace, startTimeFromTimeOriginRef, requestNavigation]);
    return useMemo(() => ({
        push,
        refresh,
        replace,
        back,
        base,
        startTimeFromTimeOriginRef,
    }), [push, refresh, replace, back, base, startTimeFromTimeOriginRef]);
}
/**
 * The application-relative path of the previous history entry when it was
 * created by this router session. Exposed as a separate hook rather than a
 * Router field so that per-navigation data never changes the referential
 * identity of the object returned by useRouter().
 */
export function useRouterBackPath() {
    return getCurrentRouterSessionBackPath();
}
/**
 * Returns true if the router should open a non-SPA route in a new tab.
 * @returns True if the router should open a non-SPA route in a new tab, false otherwise.
 */
export function useShouldOpenNonSpaRouteInNewTab() {
    return useContext(RouterContext)?.openNonSpaRouteInNewTab ?? false;
}
export function useResolveNavigationTarget() {
    const { parser, base } = useWouterRouter();
    const spaRoutes = useContext(RouterContext)?.spaRoutes ?? EMPTY_ARRAY;
    return useCallback((path) => resolveNavigationTarget(path, base, spaRoutes, parser), [base, parser, spaRoutes]);
}
/**
 * Returns a function that returns true if the current URL is in the SPA routes.
 * @returns A function that returns true if the current URL is in the SPA routes, false otherwise.
 */
export function useIsInSpa() {
    const { parser } = useWouterRouter();
    // This is intentionally optional bc SPA routes are not required
    const spaRoutes = useContext(RouterContext)?.spaRoutes ?? EMPTY_ARRAY;
    return useCallback((pathname) => isSPARoute(spaRoutes, parser, pathname), [spaRoutes, parser]);
}
/**
 * Returns a function that returns true if the current context is for a SPA app.
 * @returns A function that returns true if the current context is for a SPA app, false otherwise.
 */
export function useIsSpaApp() {
    // This is intentionally optional bc SPA routes are not required
    return !!useContext(RouterContext)?.spaRoutes;
}
/**
 * Returns the SPA route params that matches the given SPA pathname.
 * @param pathname - The pathname to match.
 * @returns The SPA route params that matches the given pathname, or undefined if no match is found.
 */
export function useSpaRouteParams(pathname) {
    const { parser } = useWouterRouter();
    // This is intentionally optional bc SPA routes are not required
    const spaRoutes = useContext(RouterContext)?.spaRoutes ?? EMPTY_ARRAY;
    const paramsMatchRef = useRef({});
    // The result of `matchRoute` is not referentially stable, so we need to memoize a match.
    if (!paramsMatchRef.current[pathname]) {
        const matchedRoute = matchRoute(spaRoutes, pathname, parser);
        if (matchedRoute) {
            paramsMatchRef.current[pathname] = matchedRoute.params;
        }
    }
    return paramsMatchRef.current[pathname];
}
//# sourceMappingURL=provider.js.map